import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBackToDashboard } from '../../utils/navigationUtils';
import {
  ArrowLeft,
  User,
  FileText,
  MessageSquare,
  Activity,
  Award,
  Eye,
  Download,
  X,
  Calendar,
  MapPin,
  Phone,
  Mail,
  CreditCard,
  Building,
  Stethoscope,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Star,
  MessageCircle,
  Image,
  FileIcon,
  Loader,
  Search,
  Filter,
  MoreVertical,
  Reply,
  Shield,
  History,
  ClipboardList
} from 'lucide-react';
import { 
  getPatientApplication,
  getApplicationSanctions,
  acknowledgeKYP,
  acknowledgeSanctionLetter
} from '../../services/applicationService';
import { initiateDischargeProcess } from '../../services/hospitalService';
import DiscussionComponent from '../../component/application/DiscussionComponent';
import KYPPreviewModal from '../../components/common/KYPPreviewModal';
import FALPreviewModal from '../../components/common/FALPreviewModal';

const LeadApplicationDetails = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const goBackToDashboard = useBackToDashboard();

  // State management
  const [activeTab, setActiveTab] = useState('details');
  const [application, setApplication] = useState(null);
  const [sanctions, setSanctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [ackActionLoading, setAckActionLoading] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Modal states
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showLetterModal, setShowLetterModal] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showDischargeModal, setShowDischargeModal] = useState(false);
  const [processingDischarge, setProcessingDischarge] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [challengeContext, setChallengeContext] = useState(null); // { type: 'kyp' | 'sanction', letter?: obj }
  const [challengeComment, setChallengeComment] = useState('');
  const [showKYPPreview, setShowKYPPreview] = useState(false);
  const [kypPreviewData, setKypPreviewData] = useState(null); // holds { kypData }
  const [showFALPreview, setShowFALPreview] = useState(false);

  // Helper selectors parity
  const getKYPAck = useCallback(() => application?.workflow?.medicalVerification?.kypAcknowledgement || null, [application]);
  const getKYPDoctorApproval = useCallback(() => application?.workflow?.medicalVerification?.kypApproval || null, [application]);
  const getSanctionAck = useCallback((sanction) => sanction?.hospitalAcknowledgement || null, []);

  // Enhance tabs dynamically (add discharge tab if final approval letter accepted and hospital ack accepted)
  const isFALAcceptedByHospital = () => {
    // Find approval sanction and check acknowledgement accepted
    const approval = sanctions.find(s => s.type === 'approval');
    return approval && approval.hospitalAcknowledgement?.status === 'accepted';
  };

  let tabs = [
    { id: 'details', label: 'Application Details', icon: User, count: null },
    { id: 'documents', label: 'Documents', icon: FileText, count: null },
    { id: 'discussions', label: 'Discussions', icon: MessageSquare, count: unreadCount },
    { id: 'status', label: 'Status Timeline', icon: Activity, count: null },
    { id: 'sanctions', label: 'Sanctions & Letters', icon: Award, count: null }
  ];

  if (isFALAcceptedByHospital() && !tabs.find(t => t.id === 'discharge')) {
    tabs = [...tabs, { id: 'discharge', label: 'Discharge (Coming Soon)', icon: Activity, count: null }];
  }

  const loadApplicationData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getPatientApplication(applicationId);
      if (result.success) {
        setApplication(result.application);
      } else {
        console.error('Error loading application:', result.error);
      }
    } catch (error) {
      console.error('Error loading application:', error);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  const loadSanctions = useCallback(async () => {
    try {
      const result = await getApplicationSanctions(applicationId);
      if (result.success) {
        setSanctions(result.sanctions);
      }
    } catch (error) {
      console.error('Error loading sanctions:', error);
    }
  }, [applicationId]);

  useEffect(() => {
    loadApplicationData();
  }, [loadApplicationData]);

  useEffect(() => {
    if (activeTab === 'sanctions') {
      loadSanctions();
    }
  }, [activeTab, loadSanctions]);

  const handleUnreadCountChange = (count) => {
    setUnreadCount(count);
  };

  const openDocument = (document) => {
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  const openLetter = (letter) => {
    setSelectedLetter(letter);
    setShowLetterModal(true);
  };

  const handleInitiateDischargeProcess = async () => {
    setProcessingDischarge(true);
    try {
      const result = await initiateDischargeProcess(applicationId, application.hospitalId, {
        kypCompleted: true,
        initiatedBy: 'hospital',
        initiatedAt: new Date()
      });
      
      if (result.success) {
        // Refresh application data to show updated status
        await loadApplicationData();
        setShowDischargeModal(false);
        alert('Discharge process initiated successfully!');
      } else {
        alert('Failed to initiate discharge process: ' + result.error);
      }
    } catch (error) {
      console.error('Error initiating discharge process:', error);
      alert('Error initiating discharge process. Please try again.');
    } finally {
      setProcessingDischarge(false);
    }
  };

  const openChallengeModal = (type, letter = null) => {
    setChallengeContext({ type, letter });
    setChallengeComment('');
    setShowChallengeModal(true);
  };

  const submitChallenge = async () => {
    if (!challengeContext) return;
    const { type, letter } = challengeContext;
    try {
      if (type === 'kyp') {
        setAckActionLoading(prev => ({ ...prev, kyp: true }));
        const res = await acknowledgeKYP(applicationId, 'challenged', challengeComment || '');
        if (!res.success) throw new Error(res.error || 'Failed to challenge KYP');
      } else if (type === 'sanction' && letter) {
        setAckActionLoading(prev => ({ ...prev, [letter.id]: true }));
        const res = await acknowledgeSanctionLetter(applicationId, letter.id, 'challenged', challengeComment || '');
        if (!res.success) throw new Error(res.error || 'Failed to challenge letter');
      }
      await loadApplicationData();
      await loadSanctions();
      setShowChallengeModal(false);
    } catch (e) {
      console.error(e);
      alert(e.message || 'Challenge failed');
    } finally {
      if (challengeContext?.type === 'kyp') {
        setAckActionLoading(prev => ({ ...prev, kyp: false }));
      } else if (challengeContext?.letter) {
        setAckActionLoading(prev => ({ ...prev, [challengeContext.letter.id]: false }));
      }
    }
  };

  const handleKYPAcknowledgement = async (status) => {
    try {
      setAckActionLoading(prev => ({ ...prev, kyp: true }));
      const res = await acknowledgeKYP(applicationId, status, '');
      if (!res.success) throw new Error(res.error || 'Failed to update KYP acknowledgement');
      await loadApplicationData();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Action failed');
    } finally {
      setAckActionLoading(prev => ({ ...prev, kyp: false }));
    }
  };

  const handleSanctionAcknowledgement = async (letter, status) => {
    if (!letter) return;
    try {
      setAckActionLoading(prev => ({ ...prev, [letter.id]: true }));
      const res = await acknowledgeSanctionLetter(applicationId, letter.id, status, '');
      if (!res.success) throw new Error(res.error || 'Failed to update acknowledgement');
      await loadSanctions();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Action failed');
    } finally {
      setAckActionLoading(prev => ({ ...prev, [letter.id]: false }));
    }
  };

  const handleLetterDownload = async (letter) => {
    try {
      if (!letter) return;
      const ack = letter.type === 'kyp' ? getKYPAck() : getSanctionAck(letter);
      if (ack && ack.status !== 'accepted') {
        alert('Download available after acceptance.');
        return;
      }
      if (letter.type === 'kyp' && !letter.url) {
        // dynamic generation similar to hospital openLetter -> handleKYPPreview
        setKypPreviewData({ kypData: application?.workflow?.medicalVerification?.kypData || {} });
        setShowKYPPreview(true);
        return;
      }
      const link = document.createElement('a');
      link.href = letter.url;
      link.download = letter.fileName || `${letter.title||'Letter'}.pdf`;
      link.click();
    } catch (e) {
      console.error('Download failed', e);
      alert('Failed to download letter');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'submitted':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending_documents':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Update status timeline helper for derived policy approval
  const isKypApprovedByDoctor = application?.workflow?.medicalVerification?.kypApproval?.status === 'approved';
  const reassign = application?.workflow?.medicalVerification?.kypReassignment;
  const reassignTargets = reassign?.targets || (reassign?.target ? [reassign.target] : []);
  const reassignIncludesPolicy = reassignTargets.includes('policy') || reassign?.target === 'policy' || reassign?.target === 'both';
  const policyReassignCompleted = reassignIncludesPolicy && ['policy_completed','completed'].includes(reassign?.status);
  const policyBaseApproved = application?.workflow?.policyVerification?.status === 'approved';
  const policyDerivedApproved = policyBaseApproved || isKypApprovedByDoctor || policyReassignCompleted;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-teal-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading application details...</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Application Not Found</h3>
          <p className="text-gray-600 mb-4">The requested application could not be found.</p>
          <button 
            onClick={() => navigate(-1)}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Go Back</span>
          </button>
        </div>
      </div>
    );
  }

  const renderApplicationDetails = () => (
    <div className="space-y-6">
      {/* Patient Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-teal-100 p-2 rounded-lg">
            <User className="h-5 w-5 text-teal-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Patient Information</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500">Patient Name</label>
            <p className="text-lg font-semibold text-gray-900">{application.patient?.name}</p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-500">Age & Gender</label>
            <p className="text-gray-900">{application.patient?.age} years, {application.patient?.gender}</p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-500">Contact</label>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Phone className="h-3 w-3 text-gray-400" />
                <p className="text-sm text-gray-900">{application.patient?.phone}</p>
              </div>
              {application.patient?.email && (
                <div className="flex items-center space-x-2">
                  <Mail className="h-3 w-3 text-gray-400" />
                  <p className="text-sm text-gray-900">{application.patient?.email}</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Aadhar Number</label>
            <p className="text-gray-900 font-mono">****-****-{application.patient?.kyc?.aadharNumber?.slice(-4)}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Address</label>
            <div className="flex items-start space-x-2">
              <MapPin className="h-3 w-3 text-gray-400 mt-1" />
              <p className="text-sm text-gray-900">{application.patient?.address}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Medical History - NEW SECTION */}
      {(application.medicalHistory || application.surgicalHistory || application.medicalHistoryDocsCount > 0) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-orange-100 p-2 rounded-lg">
              <History className="h-5 w-5 text-orange-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Medical History</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {application.medicalHistory && (
              <div>
                <label className="text-sm font-medium text-gray-500">Medical History</label>
                <p className="text-gray-900 mt-1">{application.medicalHistory}</p>
              </div>
            )}

            {application.surgicalHistory && (
              <div>
                <label className="text-sm font-medium text-gray-500">Surgical History</label>
                <p className="text-gray-900 mt-1">{application.surgicalHistory}</p>
              </div>
            )}
          </div>

          {/* Medical History Documents */}
          {application.medicalHistoryDocsCount > 0 && (
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-500">Medical History Documents</label>
              <p className="text-sm text-gray-600 mt-1">
                {application.medicalHistoryDocsCount} document(s) uploaded
              </p>
            </div>
          )}
        </div>
      )}

      {/* Medical Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Stethoscope className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Medical Information</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Diagnosis</label>
              <p className="text-gray-900 font-medium">{application.medical?.diagnosis}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Treatment Type</label>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                application.medical?.treatmentType === 'surgical' 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {application.medical?.treatmentType?.charAt(0).toUpperCase() + application.medical?.treatmentType?.slice(1)}
              </span>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Procedure Details</label>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                application.medical?.treatmentType === 'surgical' 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {application.medical?.procedureDetails}
              </span>
            </div>

            {/* NEW: Treatment Plan */}
            {application.treatmentPlanned && (
              <div>
                <label className="text-sm font-medium text-gray-500">Treatment Plan</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  application.treatmentPlanned === 'emergency' 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {application.treatmentPlanned?.charAt(0).toUpperCase() + application.treatmentPlanned?.slice(1)}
                </span>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-500">Consulting Doctor</label>
              <p className="text-gray-900">{application.medical?.doctor?.name}</p>
              <p className="text-sm text-gray-600">{application.medical?.doctor?.designation}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Procedure Details</label>
              <p className="text-gray-900">{application.medical?.procedureDetails}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Admission Date</label>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-3 w-3 text-gray-400" />
                  <p className="text-sm text-gray-900">{application.medical?.admissionDate}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Discharge Date</label>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-3 w-3 text-gray-400" />
                  <p className="text-sm text-gray-900">{application.medical?.dischargeDate || 'TBD'}</p>
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Estimated Cost</label>
              <p className="text-lg font-semibold text-gray-900">₹{application.medical?.estimatedCost?.toLocaleString('en-IN')}</p>
            </div>

            {/* UPDATED: Treatment History - now shows new structure */}
            {application.medical?.treatments && application.medical.treatments.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-500 mb-2 block">Additional Diagnoses</label>
                <div className="space-y-2">
                  {application.medical.treatments.map((treatment, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">{treatment.diagnosisName || treatment.treatmentName}</p>
                          {treatment.treatmentType && (
                            <p className="text-sm text-gray-600">Type: {treatment.treatmentType}</p>
                          )}
                          {treatment.procedureName && (
                            <p className="text-sm text-gray-600">Procedure: {treatment.procedureName}</p>
                          )}
                          <p className="text-sm text-gray-600">{treatment.description}</p>
                        </div>
                        {/* Show cost and date only if they exist (backward compatibility) */}
                        {(treatment.cost || treatment.date) && (
                          <div className="text-right">
                            {treatment.cost && <p className="text-sm font-medium text-gray-900">₹{treatment.cost?.toLocaleString('en-IN')}</p>}
                            {treatment.date && <p className="text-xs text-gray-500">{treatment.date}</p>}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* NEW: Additional Remarks */}
            {application.remarks && (
              <div>
                <label className="text-sm font-medium text-gray-500">Additional Remarks</label>
                <p className="text-gray-900">{application.remarks}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Policy Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-green-100 p-2 rounded-lg">
            <Shield className="h-5 w-5 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Insurance Policy</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500">Policy Number</label>
            <p className="text-gray-900 font-mono">{application.policy?.number}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Insurance Company</label>
            <p className="text-gray-900">{application.policy?.insuranceCompany}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Policy Type</label>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              application.policy?.type === 'corporate' 
                ? 'bg-purple-100 text-purple-800' 
                : 'bg-blue-100 text-blue-800'
            }`}>
              {application.policy?.type?.charAt(0).toUpperCase() + application.policy?.type?.slice(1)}
            </span>
          </div>

          {/* REMOVED: Coverage Amount as requested */}

          <div>
            <label className="text-sm font-medium text-gray-500">Policy Period</label>
            <p className="text-gray-900">{application.policy?.startDate} to {application.policy?.endDate}</p>
          </div>

          {application.policy?.tpaName && (
            <div>
              <label className="text-sm font-medium text-gray-500">TPA</label>
              <p className="text-gray-900">{application.policy.tpaName}</p>
            </div>
          )}
        </div>
      </div>

      {/* Policy Holder Information */}
      {application.policyHolder && !application.policyHolder.isSelf && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-purple-100 p-2 rounded-lg">
              <User className="h-5 w-5 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Policy Holder Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500">Policy Holder Name</label>
              <p className="text-gray-900">{application.policyHolder.name}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Age & Gender</label>
              <p className="text-gray-900">{application.policyHolder.age} years, {application.policyHolder.gender}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Relationship</label>
              <p className="text-gray-900">{application.policyHolder.relationshipWithPatient}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Contact</label>
              <p className="text-gray-900">{application.policyHolder.phone}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Nominee</label>
              <p className="text-gray-900">{application.policyHolder.nominee?.name}</p>
              <p className="text-sm text-gray-600">{application.policyHolder.nominee?.relationshipWithNominee}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderDocuments = () => {
    // Organize documents by category
    const documentCategories = {
      'Patient KYC': [],
      'Policy Holder KYC': [],
      'Policy Documents': [],
      'Medical History': [],
      'Treatment Documents': []
    };

    // Process documents and categorize them
    if (application.documents) {
      Object.entries(application.documents).forEach(([key, doc]) => {
        if (key.includes('patient') && (key.includes('Aadhar') || key.includes('Pan') || key.includes('Address'))) {
          documentCategories['Patient KYC'].push({ key, ...doc });
        } else if (key.includes('policyHolder') && (key.includes('Aadhar') || key.includes('Pan') || key.includes('Address'))) {
          documentCategories['Policy Holder KYC'].push({ key, ...doc });
        } else if (key.includes('policy')) {
          documentCategories['Policy Documents'].push({ key, ...doc });
        } else if (key.includes('medicalHistory')) {
          documentCategories['Medical History'].push({ key, ...doc });
        } else if (key.includes('treatment')) {
          documentCategories['Treatment Documents'].push({ key, ...doc });
        }
      });
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Application Documents</h2>
          <div className="text-sm text-gray-600">
            {Object.keys(application.documents || {}).length} document(s) uploaded
          </div>
        </div>

        {Object.entries(documentCategories).map(([category, docs]) => (
          docs.length > 0 && (
            <div key={category} className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <ClipboardList className="h-5 w-5 text-gray-600" />
                <span>{category}</span>
                <span className="text-sm font-normal text-gray-500">({docs.length})</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {docs.map((doc) => (
                  <div key={doc.key} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-5 w-5 text-blue-500" />
                        <h4 className="font-medium text-gray-900 text-sm">
                          {doc.key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </h4>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => openDocument(doc)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="View Document"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = doc.url;
                            link.download = doc.fileName;
                            link.click();
                          }}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="Download Document"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      <p className="truncate">{doc.fileName}</p>
                      <p>Size: {(doc.size / 1024).toFixed(1)} KB</p>
                      <p>Uploaded: {formatDate(doc.uploadedAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ))}

        {Object.keys(application.documents || {}).length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents</h3>
            <p className="text-gray-600">No documents have been uploaded for this application.</p>
          </div>
        )}
      </div>
    );
  };

  const renderStatusTimeline = () => {
    const statusHistory = [
      {
        status: 'submitted',
        title: 'Application Submitted',
        description: 'Hospital submitted the patient application',
        timestamp: application.submittedAt,
        completed: true,
        icon: FileText,
        color: 'blue'
      },
      {
        status: 'medical_review',
        title: 'Medical Review',
        description: 'Medical team reviewing patient documents',
        timestamp: application.workflow?.medicalVerification?.verifiedAt,
        completed: application.workflow?.medicalVerification?.status === 'approved',
        icon: Stethoscope,
        color: 'green'
      },
      {
        status: 'policy_review',
        title: 'Policy Verification',
        description: 'Policy team verifying insurance details',
        timestamp: application.workflow?.policyVerification?.verifiedAt,
        completed: policyDerivedApproved,
        icon: Shield,
        color: 'purple'
      },
      {
        status: 'kyc_verification',
        title: 'KYC Verification',
        description: 'Patient identity and documents verification',
        timestamp: application.workflow?.kycVerification?.verifiedAt,
        completed: application.workflow?.kycVerification?.status === 'approved',
        icon: User,
        color: 'orange'
      },
      {
        status: 'approved',
        title: 'Application Approved',
        description: 'Application approved for processing',
        timestamp: application.approvedAt,
        completed: application.status === 'approved',
        icon: CheckCircle,
        color: 'green'
      }
    ];

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Application Status Timeline</h2>
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(application.status)}`}>
            {application.status?.replace('_', ' ').charAt(0).toUpperCase() + application.status?.replace('_', ' ').slice(1)}
          </span>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="space-y-6">
            {statusHistory.map((item, index) => {
              const Icon = item.icon;
              const isActive = index === statusHistory.findIndex(s => !s.completed);
              const isCompleted = item.completed;

              return (
                <div key={item.status} className="relative">
                  {index < statusHistory.length - 1 && (
                    <div className={`absolute left-4 top-8 w-0.5 h-12 ${
                      isCompleted ? 'bg-green-300' : 'bg-gray-200'
                    }`} />
                  )}
                  
                  <div className="flex items-start space-x-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                      isCompleted 
                        ? 'bg-green-100 border-green-500' 
                        : isActive 
                        ? 'bg-yellow-100 border-yellow-500' 
                        : 'bg-gray-100 border-gray-300'
                    }`}>
                      <Icon className={`h-4 w-4 ${
                        isCompleted 
                          ? 'text-green-600' 
                          : isActive 
                          ? 'text-yellow-600' 
                          : 'text-gray-400'
                      }`} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className={`font-medium ${
                          isCompleted ? 'text-gray-900' : isActive ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {item.title}
                        </h3>
                        {item.timestamp && (
                          <span className="text-sm text-gray-500">
                            {formatDate(item.timestamp)}
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${
                        isCompleted ? 'text-gray-600' : isActive ? 'text-gray-600' : 'text-gray-400'
                      }`}>
                        {item.description}
                      </p>
                      
                      {/* Workflow comments */}
                      {item.status === 'medical_review' && application.workflow?.medicalVerification?.comments && (
                        <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                          <p className="text-sm text-green-800">{application.workflow.medicalVerification.comments}</p>
                        </div>
                      )}
                      
                      {item.status === 'policy_review' && application.workflow?.policyVerification?.comments && (
                        <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200">
                          <p className="text-sm text-purple-800">{application.workflow.policyVerification.comments}</p>
                        </div>
                      )}
                      
                      {item.status === 'kyc_verification' && application.workflow?.kycVerification?.comments && (
                        <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                          <p className="text-sm text-orange-800">{application.workflow.kycVerification.comments}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Current Status Details */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Status Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className={`inline-flex p-3 rounded-full mb-2 ${
                application.workflow?.medicalVerification?.status === 'approved' 
                  ? 'bg-green-100' 
                  : application.workflow?.medicalVerification?.status === 'rejected'
                  ? 'bg-red-100'
                  : 'bg-yellow-100'
              }`}>
                <Stethoscope className={`h-6 w-6 ${
                  application.workflow?.medicalVerification?.status === 'approved' 
                    ? 'text-green-600' 
                    : application.workflow?.medicalVerification?.status === 'rejected'
                    ? 'text-red-600'
                    : 'text-yellow-600'
                }`} />
              </div>
              <h4 className="font-medium text-gray-900">Medical Review</h4>
              <p className="text-sm text-gray-600">
                {application.workflow?.medicalVerification?.status === 'approved' 
                  ? 'Approved' 
                  : application.workflow?.medicalVerification?.status === 'rejected'
                  ? 'Rejected'
                  : 'Pending Review'}
              </p>
            </div>

            <div className="text-center">
              <div className={`inline-flex p-3 rounded-full mb-2 ${
                application.workflow?.policyVerification?.status === 'approved' 
                  ? 'bg-green-100' 
                  : application.workflow?.policyVerification?.status === 'rejected'
                  ? 'bg-red-100'
                  : 'bg-yellow-100'
              }`}>
                <Shield className={`h-6 w-6 ${
                  application.workflow?.policyVerification?.status === 'approved' 
                    ? 'text-green-600' 
                    : application.workflow?.policyVerification?.status === 'rejected'
                    ? 'text-red-600'
                    : 'text-yellow-600'
                }`} />
              </div>
              <h4 className="font-medium text-gray-900">Policy Review</h4>
              <p className="text-sm text-gray-600">
                {application.workflow?.policyVerification?.status === 'approved' 
                  ? 'Approved' 
                  : application.workflow?.policyVerification?.status === 'rejected'
                  ? 'Rejected'
                  : 'Pending Review'}
              </p>
            </div>

            <div className="text-center">
              <div className={`inline-flex p-3 rounded-full mb-2 ${
                application.workflow?.kycVerification?.status === 'approved' 
                  ? 'bg-green-100' 
                  : application.workflow?.kycVerification?.status === 'rejected'
                  ? 'bg-red-100'
                  : 'bg-yellow-100'
              }`}>
                <User className={`h-6 w-6 ${
                  application.workflow?.kycVerification?.status === 'approved' 
                    ? 'text-green-600' 
                    : application.workflow?.kycVerification?.status === 'rejected'
                    ? 'text-red-600'
                    : 'text-yellow-600'
                }`} />
              </div>
              <h4 className="font-medium text-gray-900">KYC Verification</h4>
              <p className="text-sm text-gray-600">
                {application.workflow?.kycVerification?.status === 'approved' 
                  ? 'Approved' 
                  : application.workflow?.kycVerification?.status === 'rejected'
                  ? 'Rejected'
                  : 'Pending Review'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSanctions = () => {
    const availableLetters = [
      { id: 'kyp', title: 'KYP Letter', description: 'Know Your Patient verification letter', icon: User, color: 'blue' },
      { id: 'approval', title: 'Final Approval Letter', description: 'Application approval confirmation', icon: CheckCircle, color: 'green' },
      { id: 'disbursal', title: 'Disbursal Letter', description: 'Treatment cost disbursal authorization', icon: CreditCard, color: 'purple' },
      { id: 'closure', title: 'Case Closure Letter', description: 'Final case closure and settlement', icon: Award, color: 'orange' }
    ];

    const canInitiateDischarge = application.workflow?.medicalVerification?.kypGenerated && 
      application.status !== 'discharge_process' && 
      application.status !== 'closed' && !application.dischargeInitiated;

    const openLetter = (letter) => {
      if (letter.type === 'kyp' && !letter.url) {
        setKypPreviewData({ kypData: application?.workflow?.medicalVerification?.kypData || {} });
        setShowKYPPreview(true);
        return;
      }
      setSelectedLetter(letter);
      setShowLetterModal(true);
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Documents & Letters</h2>
            <p className="text-sm text-gray-600">Download official letters and documents</p>
          </div>
          {canInitiateDischarge && (
            <button onClick={() => setShowDischargeModal(true)} className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2">
              <Activity className="h-4 w-4" />
              <span>Initiate Discharge Process</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {availableLetters.map(letter => {
            const Icon = letter.icon;
            let existingLetter;
            if (letter.id === 'kyp' && application.workflow?.medicalVerification?.kypGenerated && getKYPDoctorApproval()?.status === 'approved') {
              existingLetter = { id: 'kyp', type: 'kyp', title: 'KYP Letter', url: null, fileName: 'KYP-Letter.pdf', generatedAt: application.workflow.medicalVerification.kypGeneratedAt || application.workflow.medicalVerification.verifiedAt, generatedBy: 'Medical Team', isKYPLetter: true };
            } else {
              existingLetter = sanctions.find(s => s.type === letter.id);
            }
            const isAvailable = Boolean(existingLetter);
            const kypAck = letter.id === 'kyp' ? getKYPAck() : null;
            const sanctionAck = existingLetter && letter.id !== 'kyp' ? getSanctionAck(existingLetter) : null;
            const disabledDownload = letter.id === 'kyp' ? (kypAck?.status !== 'accepted') : (sanctionAck ? sanctionAck.status !== 'accepted' : false);
            const hideKYPActions = letter.id === 'kyp' && kypAck?.status === 'accepted';
            const hideSanctionActions = letter.id !== 'kyp' && sanctionAck && sanctionAck.status === 'accepted';
            return (
              <div key={letter.id} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${isAvailable ? `bg-${letter.color}-100` : 'bg-gray-100'}`}> <Icon className={`h-5 w-5 ${isAvailable ? `text-${letter.color}-600` : 'text-gray-400'}`} /> </div>
                    <div>
                      <h3 className={`font-medium ${isAvailable ? 'text-gray-900' : 'text-gray-500'}`}>{letter.title}</h3>
                      <p className={`text-sm ${isAvailable ? 'text-gray-600' : 'text-gray-400'}`}>{letter.description}</p>
                      {letter.id === 'kyp' && getKYPDoctorApproval()?.status === 'rejected' && (
                        <p className="text-xs text-red-600 mt-1">Doctor rejected KYP: {getKYPDoctorApproval()?.reason || 'No reason'}</p>
                      )}
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isAvailable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{isAvailable ? 'Available' : 'Pending'}</span>
                </div>
                {existingLetter ? (
                  <div className="space-y-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-800">Letter Available</p>
                          <p className="text-xs text-green-600">Generated on {formatDate(existingLetter.generatedAt)}</p>
                          {letter.id === 'kyp' && (
                            <p className="text-xs mt-1">Acknowledgement: <span className={`${kypAck?.status === 'accepted' ? 'text-green-700' : kypAck?.status === 'challenged' ? 'text-orange-700' : 'text-gray-700'}`}>{(kypAck?.status || 'pending').toUpperCase()}</span>{kypAck?.comment ? ` • ${kypAck.comment}` : ''}</p>
                          )}
                          {letter.id !== 'kyp' && sanctionAck && (
                            <p className="text-xs mt-1">Acknowledgement: <span className={`${sanctionAck.status === 'accepted' ? 'text-green-700' : sanctionAck.status === 'challenged' ? 'text-orange-700' : 'text-gray-700'}`}>{(sanctionAck.status || 'pending').toUpperCase()}</span>{sanctionAck.comment ? ` • ${sanctionAck.comment}` : ''}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button onClick={() => openLetter(existingLetter)} className="text-blue-600 hover:text-blue-800 text-sm"><Eye className="h-4 w-4" /></button>
                          <button onClick={() => handleLetterDownload(existingLetter)} disabled={disabledDownload} className={`px-3 py-1 rounded text-sm flex items-center space-x-1 ${disabledDownload ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}><Download className="h-3 w-3" /><span>Download</span></button>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center space-x-2">
                        {letter.id === 'kyp' && !hideKYPActions && (
                          <>
                            <button onClick={() => handleKYPAcknowledgement('accepted')} disabled={ackActionLoading.kyp} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 disabled:opacity-50">Accept</button>
                            <button onClick={() => openChallengeModal('kyp')} disabled={ackActionLoading.kyp} className="text-xs bg-orange-600 text-white px-2 py-1 rounded hover:bg-orange-700 disabled:opacity-50">Challenge</button>
                          </>
                        )}
                        {letter.id !== 'kyp' && !hideSanctionActions && (
                          <>
                            <button onClick={() => handleSanctionAcknowledgement(existingLetter, 'accepted')} disabled={ackActionLoading[existingLetter.id]} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 disabled:opacity-50">Accept</button>
                            <button onClick={() => openChallengeModal('sanction', existingLetter)} disabled={ackActionLoading[existingLetter.id]} className="text-xs bg-orange-600 text-white px-2 py-1 rounded hover:bg-orange-700 disabled:opacity-50">Challenge</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500">
                      {letter.id === 'kyp' ? (application.workflow?.medicalVerification?.kypGenerated ? (getKYPDoctorApproval()?.status === 'rejected' ? 'KYP letter was rejected by doctor' : 'Letter will be available once doctor approves the KYP') : 'Letter will be available once KYP is generated by medical team') : letter.id === 'approval' ? 'Letter will be available once all verifications are completed' : letter.id === 'disbursal' ? 'Letter will be available once application is approved' : 'Letter will be available once case is closed'}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {sanctions.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Document History</h3>
            <div className="space-y-3">
              {sanctions.map(sanction => {
                const ack = getSanctionAck(sanction);
                return (
                  <div key={sanction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-gray-600" />
                      <div>
                        <p className="font-medium text-gray-900">{sanction.title}</p>
                        <p className="text-sm text-gray-600">Generated by {sanction.generatedBy} on {formatDate(sanction.generatedAt)}</p>
                        {ack && <p className="text-xs mt-1">Ack: <span className={`${ack.status === 'accepted' ? 'text-green-700' : ack.status === 'challenged' ? 'text-orange-700' : 'text-gray-700'}`}>{(ack.status || 'pending').toUpperCase()}</span>{ack.comment ? ` • ${ack.comment}` : ''}</p>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button onClick={() => openLetter(sanction)} className="text-blue-600 hover:text-blue-800" title="View Letter"><Eye className="h-4 w-4" /></button>
                      <button onClick={() => handleLetterDownload(sanction)} disabled={ack && ack.status !== 'accepted'} className={`${ack && ack.status !== 'accepted' ? 'text-gray-400 cursor-not-allowed' : 'text-green-600 hover:text-green-800'}`} title="Download Letter"><Download className="h-4 w-4" /></button>
                      {ack && ack.status !== 'accepted' && (
                        <>
                          <button onClick={() => handleSanctionAcknowledgement(sanction, 'accepted')} disabled={ackActionLoading[sanction.id] || ack.status === 'challenged'} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 disabled:opacity-50">Accept</button>
                          <button onClick={() => openChallengeModal('sanction', sanction)} disabled={ackActionLoading[sanction.id]} className="text-xs bg-orange-600 text-white px-2 py-1 rounded hover:bg-orange-700 disabled:opacity-50">Challenge</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {showChallengeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Add Comments</h4>
              <p className="text-sm text-gray-600 mb-3">Please add your comments for challenging the {challengeContext?.type === 'kyp' ? 'KYP' : 'letter'}.</p>
              <textarea value={challengeComment} onChange={(e) => setChallengeComment(e.target.value)} rows="4" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Add your comments (optional)" />
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setShowChallengeModal(false)} className="px-3 py-2 border border-gray-300 rounded-lg">Cancel</button>
                <button onClick={submitChallenge} className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">Submit Challenge</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return renderApplicationDetails();
      case 'documents':
        return renderDocuments();
      case 'discussions':
        return (
          <DiscussionComponent 
            applicationId={applicationId} 
            onUnreadCountChange={handleUnreadCountChange}
            disableQna={true}
          />
        );
      case 'status':
        return renderStatusTimeline();
      case 'sanctions':
        return renderSanctions();
      default:
        return renderApplicationDetails();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </button>
              
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Application Details</h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>ID: {application.applicationId}</span>
                  <span>•</span>
                  <span>Patient: {application.patient?.name}</span>
                  <span>•</span>
                  <span>Submitted: {formatDate(application.submittedAt)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(application.status)}`}>
                {application.status?.replace('_', ' ').charAt(0).toUpperCase() + application.status?.replace('_', ' ').slice(1)}
              </span>
              
              {application.priority && application.priority !== 'normal' && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  application.priority === 'urgent' 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {application.priority.charAt(0).toUpperCase() + application.priority.slice(1)}
                </span>
              )}
              
              {application.emergency && (
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium flex items-center space-x-1">
                  <AlertTriangle className="h-3 w-3" />
                  <span>Emergency</span>
                </span>
              )}

              {/* NEW: Treatment Plan indicator */}
              {application.treatmentPlanned === 'emergency' && (
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                  Emergency Treatment
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                  {tab.count !== null && tab.count > 0 && (
                    <span className="bg-teal-100 text-teal-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </div>

      {/* Document Viewer Modal */}
      {showDocumentModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full h-5/6 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Document Viewer</h3>
                <p className="text-sm text-gray-600">{selectedDocument.fileName}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = selectedDocument.url;
                    link.download = selectedDocument.fileName;
                    link.click();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </button>
                <button
                  onClick={() => setShowDocumentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 p-4">
              {selectedDocument.fileName.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={selectedDocument.url}
                  className="w-full h-full border border-gray-300 rounded"
                  title="Document Viewer"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded">
                  <img
                    src={selectedDocument.url}
                    alt={selectedDocument.fileName}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Letter Viewer Modal */}
      {showLetterModal && selectedLetter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full h-5/6 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Letter Viewer</h3>
                <p className="text-sm text-gray-600">{selectedLetter.title}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = selectedLetter.url;
                    link.download = selectedLetter.fileName;
                    link.click();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </button>
                <button
                  onClick={() => setShowLetterModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 p-4">
              <iframe
                src={selectedLetter.url}
                className="w-full h-full border border-gray-300 rounded"
                title="Letter Viewer"
              />
            </div>
          </div>
        </div>
      )}

      {/* KYP Preview Modal */}
      <KYPPreviewModal
        isOpen={showKYPPreview}
        onClose={() => setShowKYPPreview(false)}
        application={application}
        kypData={kypPreviewData?.kypData}
        canDownload={(getKYPAck()?.status || 'pending') === 'accepted'}
      />

      {/* FAL Preview Modal (shown if triggered in future) */}
      <FALPreviewModal
        isOpen={showFALPreview}
        onClose={() => setShowFALPreview(false)}
        application={application}
        remarks={application?.workflow?.dischargeSettlement?.comments || ''}
        canDownload={true}
        template="comprehensive"
      />
    </div>
  );
};

export default LeadApplicationDetails;